import React, { useState } from "react";
import auth from "../../Account/Auth"; // adjust the import paths as needed
import be from "../../BE";
import ConnectSearchProvider from "../Contacts/ConnectSearchProvider";

function SearchContacts({ fetchContactsForPage, showModal, setShowModal }) {
  const currentYear = new Date().getFullYear();

  // ---------------------------
  // State for form fields
  // ---------------------------
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");

  // --- Decision Makers Request Logic and States ---
  const [decisionMakersView, setDecisionMakersView] = useState(false);
  const [decisionMakers, setDecisionMakers] = useState([]);
  const [decisionMakersLoading, setDecisionMakersLoading] = useState(false);

  // Fetch Decision Makers Request Logic (using company domain from comp.domain)
  const fetchDecisionMakers = async (domain) => {
    try {
      setDecisionMakersLoading(true);
      setDecisionMakersView(true);
      await auth
        .isAuthenticated()
        .then(async () => {
          await be
            .get("Leads", `/search/decision-makers`, { domain }, true, true)
            .then((response) => {
              if (response && response.profiles) {
                const profilesArray = Object.entries(response.profiles).map(
                  ([profileUrl, data]) => ({ [profileUrl]: data })
                );
                setDecisionMakers(profilesArray);
                setDecisionMakersView(true);
              } else {
                setDecisionMakers([]);
              }
            })
            .catch((error) => {
              console.error(
                "[Leads][search-decision-makers] network error:",
                error
              );
            });
        })
        .catch((error) => {
          console.error(
            "[Leads][search-decision-makers] authentication error:",
            error
          );
        });
    } catch (error) {
      console.error("[Leads][search-decision-makers] functional error:", error);
    } finally {
      setDecisionMakersLoading(false);
    }
  };

  // Company Sizes (no default)
  const companySizes = [
    { value: "1_10", label: "1 - 10" },
    { value: "11_50", label: "11 - 50" },
    { value: "51_200", label: "51 - 200" },
    { value: "201_500", label: "201 - 500" },
    { value: "501_1000", label: "501 - 1,000" },
    { value: "1001_5000", label: "1,001 - 5,000" },
    { value: "5001_10000", label: "5,001 - 10,000" },
    { value: "10001", label: "10,000+" },
  ];
  const [companySize, setCompanySize] = useState("");

  // Revenue Options
  const revenueOptions = [
    { value: "0", label: "$0" },
    { value: "1000000", label: "$1M" },
    { value: "5000000", label: "$5M" },
    { value: "10000000", label: "$10M" },
    { value: "50000000", label: "$50M" },
    { value: "100000000", label: "$100M" },
    { value: "250000000", label: "$250M" },
    { value: "500000000", label: "$500M" },
    { value: "1000000000", label: "$1B+" },
  ];
  const [revenueMin, setRevenueMin] = useState("");
  const [revenueMax, setRevenueMax] = useState("");
  const maxRevenueValue = 1000000000;

  // Year Founded options (from 1986 to currentYear)
  const yearsAsc = Array.from(
    { length: currentYear - 1986 + 1 },
    (_, i) => 1986 + i
  );
  const [yearFoundedFrom, setYearFoundedFrom] = useState("");
  const [yearFoundedTo, setYearFoundedTo] = useState("");

  // ---------------------------
  // Additional state
  // ---------------------------
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Save Contact Request Logic & State
  const [saveStatus, setSaveStatus] = useState({});

  // ---------------------------
  // Toggle Modal
  // ---------------------------
  const toggleModal = () => {
    setShowModal((prev) => !prev);
    if (showModal === true) {
      window.location.reload();
    }
  };

  // ---------------------------
  // Revenue Handlers
  // ---------------------------
  const handleRevenueMinChange = (e) => {
    const value = e.target.value;
    setRevenueMin(value);
    const numericValue = parseInt(value, 10);
    if (numericValue === maxRevenueValue) {
      setRevenueMax("");
    } else if (revenueMax && parseInt(revenueMax, 10) <= numericValue) {
      setRevenueMax("");
    }
  };

  const handleRevenueMaxChange = (e) => {
    setRevenueMax(e.target.value);
  };

  // ---------------------------
  // Year Founded Handlers
  // ---------------------------
  const handleYearFoundedFromChange = (e) => {
    const newFrom = e.target.value;
    setYearFoundedFrom(newFrom);
    if (yearFoundedTo && parseInt(yearFoundedTo, 10) <= parseInt(newFrom, 10)) {
      setYearFoundedTo("");
    }
  };

  const handleYearFoundedToChange = (e) => {
    const newTo = e.target.value;
    setYearFoundedTo(newTo);
    if (
      yearFoundedFrom &&
      parseInt(yearFoundedFrom, 10) >= parseInt(newTo, 10)
    ) {
      setYearFoundedFrom("");
    }
  };

  // ---------------------------
  // Reset Logic
  // ---------------------------
  const resetForm = () => {
    setCompany("");
    setLocation("");
    setIndustry("");
    setCompanySize("");
    setRevenueMin("");
    setRevenueMax("");
    setYearFoundedFrom("");
    setYearFoundedTo("");
    setCompanies([]);
  };

  // ---------------------------
  // Form Validation
  // ---------------------------
  const isFormEmpty = () => {
    return (
      company.trim() === "" &&
      location.trim() === "" &&
      industry === "" &&
      companySize === "" &&
      revenueMin === "" &&
      revenueMax === "" &&
      yearFoundedFrom === "" &&
      yearFoundedTo === ""
    );
  };

  // ---------------------------
  // Request to Backend for Companies
  // ---------------------------
  const fetchCompanies = async () => {
    try {
      await auth
        .isAuthenticated()
        .then(async () => {
          // Domain field removed from the query parameters
          const params = {
            name: company.trim(),
            size: companySize,
            location: location.trim(),
            industries: industry,
            min_revenue: revenueMin,
            max_revenue: revenueMax,
            year_founded_from: yearFoundedFrom,
          };
          if (yearFoundedFrom && yearFoundedTo) {
            params.year_frounded_to = yearFoundedTo;
          }
          Object.keys(params).forEach((key) => {
            if (params[key] === "" || params[key] == null) {
              delete params[key];
            }
          });
          await be
            .get("Leads", `/search/companies`, params, true, true)
            .then((response) => {
              if (response && response.companies) {
                setCompanies(response.companies);
              } else {
                setCompanies([]);
              }
            })
            .catch((error) => {
              console.error("[Leads][search-companies] network error:", error);
            });
        })
        .catch((error) => {
          console.error(
            "[Leads][search-companies] authentication error:",
            error
          );
        });
    } catch (error) {
      console.error("[Leads][search-companies] functional error:", error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (isFormEmpty()) return;
    setLoading(true);
    await fetchCompanies();
    setLoading(false);
  };

  // Helper functions
  function getCompanySizeLabel(size) {
    if (!size) return "";
    const num = Number(size);
    if (num <= 10) return "1 - 10";
    if (num <= 50) return "11 - 50";
    if (num <= 200) return "51 - 200";
    if (num <= 500) return "201 - 500";
    if (num <= 1000) return "501 - 1,000";
    if (num <= 5000) return "1,001 - 5,000";
    if (num <= 10000) return "5,001 - 10,000";
    return "10,000+";
  }

  function formatRevenue(revenue) {
    if (revenue == null || isNaN(revenue)) return "";
    const num = Number(revenue);
    if (num < 1000000) return "$0";
    if (num < 5000000) return "$1M";
    if (num < 10000000) return "$5M";
    if (num < 50000000) return "$10M";
    if (num < 100000000) return "$50M";
    if (num < 250000000) return "$100M";
    if (num < 500000000) return "$250M";
    if (num < 1000000000) return "$500M";
    return "$1B+";
  }

  function ExpandableText({ text, maxChars }) {
    const [expanded, setExpanded] = useState(false);
    if (!text) return null;
    const isTruncated = text.length > maxChars;
    const displayText =
      !expanded && isTruncated ? text.slice(0, maxChars) + "..." : text;
    return (
      <div className="tw-text-sm tw-text-gray-700">
        <p>{displayText}</p>
        {isTruncated && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="tw-text-xs tw-text-primary-700 tw-underline"
          >
            {expanded ? "less" : "more"}
          </button>
        )}
      </div>
    );
  }

  // SAVE CONTACT LOGIC – using dm.email and dm.phone_numbers
  const handleSaveContact = async (profileUrl, dm) => {
    const contactValue =
      dm.email || (dm.phone_numbers && dm.phone_numbers[0]) || "";
    if (!contactValue) return;
    const requestBody = {
      contact: contactValue,
      full_name: dm.full_name,
      ...dm,
    };
    setSaveStatus((prev) => ({
      ...prev,
      [profileUrl]: { saving: true },
    }));
    try {
      await auth
        .isAuthenticated()
        .then(async () => {
          await be
            .post("Leads", `/leads`, requestBody, true, true)
            .then((response) => {
              setSaveStatus((prev) => ({
                ...prev,
                [profileUrl]: { saving: false, saved: true },
              }));
            })
            .catch((error) => {
              console.error("[Leads][save-contact] network error:", error);
              setSaveStatus((prev) => ({
                ...prev,
                [profileUrl]: { saving: false },
              }));
            });
        })
        .catch((error) => {
          console.error("[Leads][save-contact] authentication error:", error);
          setSaveStatus((prev) => ({
            ...prev,
            [profileUrl]: { saving: false },
          }));
        });
    } catch (error) {
      console.error("[Leads][save-contact] functional error:", error);
      setSaveStatus((prev) => ({
        ...prev,
        [profileUrl]: { saving: false },
      }));
    }
  };

  return (
    <div>
      <div className="tw-flex tw-justify-start tw-items-start tw-py-2">
        <ConnectSearchProvider toggleModal={toggleModal} />
      </div>
      {/* Modal */}
      {showModal && (
        <div
          className="tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-z-50 tw-w-full tw-p-4 tw-overflow-x-hidden tw-overflow-y-auto md:tw-inset-0 tw-h-modal md:tw-h-full"
          aria-modal="true"
          role="dialog"
        >
          <div className="tw-relative tw-w-full tw-h-full tw-max-w-6xl md:tw-h-auto tw-mx-auto">
            <div className="tw-relative tw-bg-white tw-rounded-lg tw-shadow dark:tw-bg-gray-800">
              {/* Modal Header */}
              <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-rounded-t">
                <h3 className="tw-text-lg tw-font-normal tw-text-gray-500 dark:tw-text-gray-400">
                  Search Contacts
                </h3>
                <button
                  onClick={toggleModal}
                  type="button"
                  className="tw-text-gray-400 tw-bg-transparent tw-hover:tw-bg-gray-200 tw-hover:tw-text-gray-900 tw-rounded-lg tw-text-sm tw-p-1.5 tw-ml-auto tw-inline-flex tw-items-center dark:tw-hover:tw-bg-gray-600 dark:tw-hover:tw-text-white"
                >
                  <svg
                    aria-hidden="true"
                    className="tw-w-5 tw-h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  <span className="tw-sr-only">Close modal</span>
                </button>
              </div>
              {/* Modal Body */}
              <div className="tw-px-4 tw-py-6">
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-6 tw-gap-6">
                  {/* Left Column: Search Form */}
                  <form
                    className="tw-col-span-2 tw-space-y-4"
                    onSubmit={handleSearch}
                  >
                    {/* Company */}
                    <div>
                      <label
                        htmlFor="company"
                        className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                      >
                        Company
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        placeholder="Company name"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        disabled={loading}
                        className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                      />
                    </div>
                    {/* Location */}
                    <div>
                      <label
                        htmlFor="location"
                        className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                      >
                        Location
                      </label>
                      <input
                        type="text"
                        id="location"
                        name="location"
                        placeholder="City, State or Country"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        disabled={loading}
                        className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                      />
                    </div>
                    {/* Industry */}
                    <div>
                      <label
                        htmlFor="industry"
                        className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                      >
                        Industry
                      </label>
                      <select
                        id="industry"
                        name="industry"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        disabled={loading}
                        className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                      >
                        <option value="">Select Industry</option>
                        <option value="Defense & Space">Defense & Space</option>
                        <option value="Computer Hardware">
                          Computer Hardware
                        </option>
                        <option value="Computer Software">
                          Computer Software
                        </option>
                        <option value="Computer Networking">
                          Computer Networking
                        </option>
                        <option value="Internet">Internet</option>
                        <option value="Semiconductors">Semiconductors</option>
                        <option value="Telecommunications">
                          Telecommunications
                        </option>
                        <option value="Law Practice">Law Practice</option>
                        <option value="Legal Services">Legal Services</option>
                        <option value="Management Consulting">
                          Management Consulting
                        </option>
                        <option value="Biotechnology">Biotechnology</option>
                        <option value="Medical Practice">
                          Medical Practice
                        </option>
                        <option value="Hospital & Health Care">
                          Hospital & Health Care
                        </option>
                        <option value="Pharmaceuticals">Pharmaceuticals</option>
                        <option value="Veterinary">Veterinary</option>
                        <option value="Medical Device">Medical Device</option>
                        <option value="Cosmetics">Cosmetics</option>
                        <option value="Apparel & Fashion">
                          Apparel & Fashion
                        </option>
                        <option value="Sporting Goods">Sporting Goods</option>
                        <option value="Tobacco">Tobacco</option>
                        <option value="Supermarkets">Supermarkets</option>
                        <option value="Food Production">Food Production</option>
                        <option value="Consumer Electronics">
                          Consumer Electronics
                        </option>
                        <option value="Consumer Goods">Consumer Goods</option>
                        <option value="Furniture">Furniture</option>
                        <option value="Retail">Retail</option>
                        <option value="Entertainment">Entertainment</option>
                        <option value="Gambling & Casinos">
                          Gambling & Casinos
                        </option>
                        <option value="Leisure, Travel & Tourism">
                          Leisure, Travel & Tourism
                        </option>
                        <option value="Hospitality">Hospitality</option>
                        <option value="Restaurants">Restaurants</option>
                        <option value="Sports">Sports</option>
                        <option value="Food & Beverages">
                          Food & Beverages
                        </option>
                        <option value="Motion Pictures & Film">
                          Motion Pictures & Film
                        </option>
                        <option value="Broadcast Media">Broadcast Media</option>
                        <option value="Museums & Institutions">
                          Museums & Institutions
                        </option>
                        <option value="Fine Art">Fine Art</option>
                        <option value="Performing Arts">Performing Arts</option>
                        <option value="Recreational Facilities & Services">
                          Recreational Facilities & Services
                        </option>
                        <option value="Banking">Banking</option>
                        <option value="Insurance">Insurance</option>
                        <option value="Financial Services">
                          Financial Services
                        </option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Investment Banking">
                          Investment Banking
                        </option>
                        <option value="Investment Management">
                          Investment Management
                        </option>
                        <option value="Accounting">Accounting</option>
                        <option value="Construction">Construction</option>
                        <option value="Building Materials">
                          Building Materials
                        </option>
                        <option value="Architecture & Planning">
                          Architecture & Planning
                        </option>
                        <option value="Civil Engineering">
                          Civil Engineering
                        </option>
                        <option value="Aviation & Aerospace">
                          Aviation & Aerospace
                        </option>
                        <option value="Automotive">Automotive</option>
                        <option value="Chemicals">Chemicals</option>
                        <option value="Machinery">Machinery</option>
                        <option value="Mining & Metals">Mining & Metals</option>
                        <option value="Oil & Energy">Oil & Energy</option>
                        <option value="Shipbuilding">Shipbuilding</option>
                        <option value="Utilities">Utilities</option>
                        <option value="Textiles">Textiles</option>
                        <option value="Paper & Forest Products">
                          Paper & Forest Products
                        </option>
                        <option value="Railroad Manufacture">
                          Railroad Manufacture
                        </option>
                        <option value="Farming">Farming</option>
                        <option value="Ranching">Ranching</option>
                        <option value="Dairy">Dairy</option>
                        <option value="Fishery">Fishery</option>
                        <option value="Primary/Secondary Education">
                          Primary/Secondary Education
                        </option>
                        <option value="Higher Education">
                          Higher Education
                        </option>
                        <option value="Education Management">
                          Education Management
                        </option>
                        <option value="Research">Research</option>
                        <option value="Military">Military</option>
                        <option value="Legislative Office">
                          Legislative Office
                        </option>
                        <option value="Judiciary">Judiciary</option>
                        <option value="International Affairs">
                          International Affairs
                        </option>
                        <option value="Government Administration">
                          Government Administration
                        </option>
                        <option value="Executive Office">
                          Executive Office
                        </option>
                        <option value="Law Enforcement">Law Enforcement</option>
                        <option value="Public Safety">Public Safety</option>
                        <option value="Public Policy">Public Policy</option>
                        <option value="Marketing & Advertising">
                          Marketing & Advertising
                        </option>
                        <option value="Newspapers">Newspapers</option>
                        <option value="Publishing">Publishing</option>
                        <option value="Printing">Printing</option>
                        <option value="Information Services">
                          Information Services
                        </option>
                        <option value="Libraries">Libraries</option>
                        <option value="Environmental Services">
                          Environmental Services
                        </option>
                        <option value="Package/Freight Delivery">
                          Package/Freight Delivery
                        </option>
                        <option value="Individual & Family Services">
                          Individual & Family Services
                        </option>
                        <option value="Religious Institutions">
                          Religious Institutions
                        </option>
                        <option value="Civic & Social Organization">
                          Civic & Social Organization
                        </option>
                        <option value="Consumer Services">
                          Consumer Services
                        </option>
                        <option value="Transportation/Trucking/Railroad">
                          Transportation/Trucking/Railroad
                        </option>
                        <option value="Warehousing">Warehousing</option>
                        <option value="Airlines/Aviation">
                          Airlines/Aviation
                        </option>
                        <option value="Maritime">Maritime</option>
                        <option value="Information Technology & Services">
                          Information Technology & Services
                        </option>
                        <option value="Market Research">Market Research</option>
                        <option value="Public Relations & Communications">
                          Public Relations & Communications
                        </option>
                        <option value="Design">Design</option>
                        <option value="Non-profit Organization Management">
                          Non-profit Organization Management
                        </option>
                        <option value="Fundraising">Fundraising</option>
                        <option value="Program Development">
                          Program Development
                        </option>
                        <option value="Writing & Editing">
                          Writing & Editing
                        </option>
                        <option value="Staffing & Recruiting">
                          Staffing & Recruiting
                        </option>
                        <option value="Professional Training & Coaching">
                          Professional Training & Coaching
                        </option>
                        <option value="Venture Capital & Private Equity">
                          Venture Capital & Private Equity
                        </option>
                        <option value="Political Organization">
                          Political Organization
                        </option>
                        <option value="Translation & Localization">
                          Translation & Localization
                        </option>
                        <option value="Computer Games">Computer Games</option>
                        <option value="Events Services">Events Services</option>
                        <option value="Arts & Crafts">Arts & Crafts</option>
                        <option value="Electrical & Electronic Manufacturing">
                          Electrical & Electronic Manufacturing
                        </option>
                        <option value="Online Media">Online Media</option>
                        <option value="Nanotechnology">Nanotechnology</option>
                        <option value="Music">Music</option>
                        <option value="Logistics & Supply Chain">
                          Logistics & Supply Chain
                        </option>
                        <option value="Plastics">Plastics</option>
                        <option value="Computer & Network Security">
                          Computer & Network Security
                        </option>
                        <option value="Wireless">Wireless</option>
                        <option value="Alternative Dispute Resolution">
                          Alternative Dispute Resolution
                        </option>
                        <option value="Security & Investigations">
                          Security & Investigations
                        </option>
                        <option value="Facilities Services">
                          Facilities Services
                        </option>
                        <option value="Outsourcing/Offshoring">
                          Outsourcing/Offshoring
                        </option>
                        <option value="Health, Wellness & Fitness">
                          Health, Wellness & Fitness
                        </option>
                        <option value="Alternative Medicine">
                          Alternative Medicine
                        </option>
                        <option value="Media Production">
                          Media Production
                        </option>
                        <option value="Animation">Animation</option>
                        <option value="Commercial Real Estate">
                          Commercial Real Estate
                        </option>
                        <option value="Capital Markets">Capital Markets</option>
                        <option value="Think Tanks">Think Tanks</option>
                        <option value="Philanthropy">Philanthropy</option>
                        <option value="E-learning">E-learning</option>
                        <option value="Wholesale">Wholesale</option>
                        <option value="Import & Export">Import & Export</option>
                        <option value="Mechanical Or Industrial Engineering">
                          Mechanical Or Industrial Engineering
                        </option>
                        <option value="Photography">Photography</option>
                        <option value="Human Resources">Human Resources</option>
                        <option value="Business Supplies & Equipment">
                          Business Supplies & Equipment
                        </option>
                        <option value="Mental Health Care">
                          Mental Health Care
                        </option>
                        <option value="Graphic Design">Graphic Design</option>
                        <option value="International Trade & Development">
                          International Trade & Development
                        </option>
                        <option value="Wine & Spirits">Wine & Spirits</option>
                        <option value="Luxury Goods & Jewelry">
                          Luxury Goods & Jewelry
                        </option>
                        <option value="Renewables & Environment">
                          Renewables & Environment
                        </option>
                        <option value="Glass, Ceramics & Concrete">
                          Glass, Ceramics & Concrete
                        </option>
                        <option value="Packaging & Containers">
                          Packaging & Containers
                        </option>
                        <option value="Industrial Automation">
                          Industrial Automation
                        </option>
                        <option value="Government Relations">
                          Government Relations
                        </option>
                      </select>
                    </div>
                    {/* Company Size */}
                    <div>
                      <label
                        htmlFor="companySize"
                        className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                      >
                        Company Size
                      </label>
                      <select
                        id="companySize"
                        name="companySize"
                        value={companySize}
                        onChange={(e) => setCompanySize(e.target.value)}
                        disabled={loading}
                        className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                      >
                        <option value="">Select Company Size</option>
                        {companySizes.map((size) => (
                          <option key={size.value} value={size.value}>
                            {size.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Revenue Range */}
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                      <div>
                        <label
                          htmlFor="revenueMin"
                          className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                        >
                          Revenue From
                        </label>
                        <select
                          id="revenueMin"
                          name="revenueMin"
                          value={revenueMin}
                          onChange={handleRevenueMinChange}
                          disabled={loading}
                          className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                        >
                          <option value="">Select Revenue From</option>
                          {revenueOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="revenueMax"
                          className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                        >
                          Revenue To
                        </label>
                        <select
                          id="revenueMax"
                          name="revenueMax"
                          value={revenueMax}
                          onChange={handleRevenueMaxChange}
                          disabled={
                            loading ||
                            (revenueMin &&
                              parseInt(revenueMin, 10) === maxRevenueValue)
                          }
                          className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                        >
                          <option value="">Select Revenue To</option>
                          {revenueOptions
                            .filter((option) => {
                              if (!revenueMin) return true;
                              return (
                                parseInt(option.value, 10) >
                                parseInt(revenueMin, 10)
                              );
                            })
                            .map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>

                    {/* Year Founded Range */}
                    <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                      <div>
                        <label
                          htmlFor="yearFoundedFrom"
                          className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                        >
                          Year Founded From
                        </label>
                        <select
                          id="yearFoundedFrom"
                          name="yearFoundedFrom"
                          value={yearFoundedFrom}
                          onChange={handleYearFoundedFromChange}
                          disabled={loading}
                          className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                        >
                          <option value="">Select Year Founded From</option>
                          {yearsAsc.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label
                          htmlFor="yearFoundedTo"
                          className="tw-block tw-mb-2 tw-text-sm tw-font-medium tw-text-gray-900 dark:tw-text-white"
                        >
                          Year Founded To
                        </label>
                        <select
                          id="yearFoundedTo"
                          name="yearFoundedTo"
                          value={yearFoundedTo}
                          onChange={handleYearFoundedToChange}
                          disabled={
                            loading ||
                            !yearFoundedFrom ||
                            parseInt(yearFoundedFrom, 10) === currentYear
                          }
                          className="tw-bg-gray-50 tw-border tw-border-gray-300 tw-text-gray-900 tw-text-sm tw-rounded-lg tw-focus:tw-ring-primary-500 tw-focus:tw-border-primary-500 tw-block tw-w-full tw-p-2.5 dark:tw-bg-gray-700 dark:tw-border-gray-600 dark:tw-placeholder-gray-400 dark:tw-text-white dark:tw-focus:tw-ring-primary-500 dark:tw-focus:tw-border-primary-500"
                        >
                          <option value="">Select Year Founded To</option>
                          {yearFoundedFrom &&
                            yearsAsc
                              .filter(
                                (year) =>
                                  parseInt(year, 10) >
                                  parseInt(yearFoundedFrom, 10)
                              )
                              .map((year) => (
                                <option key={year} value={year}>
                                  {year}
                                </option>
                              ))}
                        </select>
                      </div>
                    </div>
                  </form>
                  {/* Right Side of Modal */}
                  <div className="tw-col-span-4 tw-overflow-scroll tw-h-[70vh]">
                    {decisionMakersView ? (
                      <div>
                        {/* Improved Back to Company List button */}
                        <button
                          onClick={() => setDecisionMakersView(false)}
                          className="tw-bg-primary-700 tw-text-white tw-px-4 tw-py-2 tw-rounded tw-mb-4"
                        >
                          Back to Company List
                        </button>
                        {decisionMakersLoading ? (
                          <div className="tw-flex tw-items-center tw-justify-center">
                            <p>Loading decision makers...</p>
                          </div>
                        ) : Object.keys(decisionMakers).length > 0 ||
                          (decisionMakers && decisionMakers.length > 0) ? (
                          decisionMakers.map((dmObj, index) => {
                            const dmKey = Object.keys(dmObj)[0];
                            const dm = dmObj[dmKey];
                            const hasContactInfo =
                              dm.email ||
                              (dm.phone_numbers && dm.phone_numbers.length > 0);
                            return (
                              <div
                                key={index}
                                className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-mb-4 tw-flex"
                              >
                                {/* Left Section: Decision Maker Main Info */}
                                <div className="tw-flex-grow tw-mr-2">
                                  <div className="tw-flex tw-items-center tw-space-x-2 tw-mb-2">
                                    <div className="tw-relative">
                                      <img
                                        src={
                                          dm.profile_picture_url ||
                                          "/app-assets/img/standard-user-pic.png"
                                        }
                                        alt={dm.full_name}
                                        className="tw-w-10 tw-h-10 tw-rounded-full"
                                      />
                                      {dm.company && dm.company.logo_url && (
                                        <img
                                          src={dm.company.logo_url}
                                          alt={`${dm.company.name} logo`}
                                          className="tw-absolute tw-bottom-0 tw-right-0 tw-w-4 tw-h-4 tw-rounded-full"
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <div className="tw-inline-flex">
                                        <h4 className="tw-text-lg tw-font-semibold">
                                          {dm.full_name}
                                        </h4>
                                        {dmKey && (
                                          <a
                                            href={dmKey}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="tw-ml-1 tw-text-primary-700"
                                          >
                                            <i className="fa-solid fa-link tw-text-xs"></i>
                                          </a>
                                        )}
                                      </div>
                                      <div className="tw-text-xs tw-text-gray-600">
                                        {dm.title}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="tw-text-sm tw-text-gray-800 tw-mt-1">
                                    {dm.headline}
                                  </div>
                                  <div className="tw-text-sm tw-text-gray-600 tw-mt-1">
                                    <i className="fa-solid fa-location-dot tw-mr-1"></i>
                                    {dm.location}
                                  </div>
                                </div>
                                {/* Right Section: Contact Info & Save Button */}
                                <div className="tw-min-w-[150px] tw-flex tw-flex-col tw-items-start tw-justify-between tw-pl-4 tw-border-l tw-border-gray-200">
                                  <div className="tw-mb-2">
                                    <div className="tw-text-xs tw-text-gray-600">
                                      Email
                                    </div>
                                    <div className="tw-text-xs tw-text-gray-800">
                                      {dm.email || "—"}
                                    </div>
                                  </div>
                                  <div className="tw-mb-2">
                                    <div className="tw-text-xs tw-text-gray-600">
                                      Phone
                                    </div>
                                    <div className="tw-text-xs tw-text-gray-800">
                                      {(dm.phone_numbers &&
                                        dm.phone_numbers[0]) ||
                                        "—"}
                                    </div>
                                  </div>
                                  <div className="tw-w-full">
                                    {!hasContactInfo ? (
                                      <button
                                        className="tw-w-full tw-text-xs tw-text-white tw-bg-gray-400 tw-rounded-full tw-px-3 tw-py-1"
                                        disabled
                                      >
                                        No Contact Info
                                      </button>
                                    ) : saveStatus[dmKey] &&
                                      saveStatus[dmKey].saved ? (
                                      <span className="tw-text-xs tw-text-green-600">
                                        Saved
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          handleSaveContact(dmKey, dm)
                                        }
                                        className="tw-w-full tw-text-xs tw-text-white tw-bg-green-600 tw-rounded-full tw-px-3 tw-py-1"
                                        disabled={
                                          saveStatus[dmKey] &&
                                          saveStatus[dmKey].saving
                                        }
                                      >
                                        {saveStatus[dmKey] &&
                                        saveStatus[dmKey].saving
                                          ? "Saving..."
                                          : "Save Contact"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                            <h4 className="tw-text-lg tw-font-semibold tw-mb-2">
                              Decision Makers
                            </h4>
                            <p className="tw-text-gray-500">
                              No decision makers found.
                            </p>
                          </div>
                        )}
                      </div>
                    ) : loading ? (
                      <div className="tw-flex tw-items-center tw-justify-center">
                        <p>Loading companies...</p>
                      </div>
                    ) : companies.length > 0 ? (
                      companies.map((comp, index) => (
                        <div
                          key={index}
                          className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-mb-4 tw-flex"
                        >
                          {/* Left Section: Company Main Info */}
                          <div className="tw-flex-grow tw-mr-2">
                            <div className="tw-flex tw-items-center tw-space-x-2">
                              {comp.logo_url && (
                                <img
                                  src={comp.logo_url}
                                  alt={`${comp.name} logo`}
                                  className="tw-w-7 tw-h-7 tw-rounded-full"
                                />
                              )}
                              <h4 className="tw-text-xl tw-font-semibold">
                                {comp.name}
                              </h4>
                              {comp.website && (
                                <a
                                  href={comp.website}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="tw-text-gray-500 tw-text-sm"
                                >
                                  <i className="fas fa-link"></i>
                                </a>
                              )}
                            </div>
                            <div className="tw-text-sm tw-text-gray-500 tw-my-2">
                              {comp.headquarter ||
                                (comp.locations && comp.locations[0])}
                            </div>
                            <div className="tw-mt-2">
                              {comp.overview && comp.overview.length > 150 ? (
                                <ExpandableText
                                  text={comp.overview}
                                  maxChars={150}
                                />
                              ) : (
                                <p className="tw-text-sm tw-text-gray-800">
                                  {comp.overview}
                                </p>
                              )}
                            </div>
                          </div>
                          {/* Right Section: Additional Info Column */}
                          <div className="tw-min-w-[200px] tw-flex tw-flex-col tw-justify-center tw-pl-4 tw-border-l tw-border-gray-200">
                            <button
                              onClick={() => fetchDecisionMakers(comp.domain)}
                              className="tw-flex tw-items-center tw-text-xs tw-text-primary-700 tw-border tw-border-primary-700 tw-rounded-full tw-px-2 tw-py-1 tw-mb-2"
                            >
                              <i className="fa-solid fa-magnifying-glass tw-mr-1"></i>
                              Search decision Makers
                            </button>
                            <div className="tw-flex tw-items-center tw-text-xs tw-text-gray-700 tw-mb-2">
                              <i className="fa-solid fa-industry tw-mr-1"></i>
                              {comp.industry}
                            </div>
                            <div className="tw-flex tw-items-center tw-text-xs tw-text-gray-700 tw-mb-2">
                              <i className="fa-solid fa-users tw-mr-1"></i>
                              {getCompanySizeLabel(comp.size)}
                            </div>
                            {comp.revenue && (
                              <div className="tw-flex tw-items-center tw-text-xs tw-text-gray-700">
                                <i className="fa-solid fa-dollar-sign tw-mr-1"></i>
                                {formatRevenue(comp.revenue)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4">
                        <h4 className="tw-text-lg tw-font-semibold tw-mb-2">
                          Search Results
                        </h4>
                        <p className="tw-text-gray-500">
                          No results to display.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* Modal Footer */}
              <div className="tw-flex tw-items-center tw-p-6 tw-space-x-4 tw-rounded-b dark:tw-border-gray-600">
                <button
                  type="reset"
                  onClick={resetForm}
                  disabled={loading}
                  className="tw-py-2.5 tw-px-5 tw-text-sm tw-font-medium tw-text-gray-900 tw-focus:tw-outline-none tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-hover:tw-bg-gray-100 tw-hover:tw-text-primary-700 tw-focus:tw-z-10 tw-focus:tw-ring-4 tw-focus:tw-ring-gray-200 dark:tw-focus:tw-ring-gray-700 dark:tw-bg-gray-800 dark:tw-text-gray-400 dark:tw-border-gray-600 dark:tw-hover:tw-text-white dark:tw-hover:tw-bg-gray-700"
                >
                  Reset
                </button>
                <button
                  type="submit"
                  onClick={handleSearch}
                  disabled={isFormEmpty() || loading}
                  className={`tw-text-white tw-bg-primary-700 tw-hover:tw-bg-primary-800 tw-focus:tw-ring-4 tw-focus:tw-outline-none tw-focus:tw-ring-primary-300 tw-font-medium tw-rounded-lg tw-text-sm tw-px-5 tw-py-2.5 tw-text-center dark:tw-bg-primary-700 dark:tw-hover:tw-bg-primary-800 dark:tw-focus:tw-ring-primary-800 ${
                    isFormEmpty() || loading
                      ? "tw-opacity-50 tw-cursor-not-allowed"
                      : ""
                  }`}
                >
                  {loading ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchContacts;
